export interface OnboardingRoutingUser {
  isOnboardingCompleted?: unknown;
  trialSubscriptionStartDateAndroid?: unknown;
  trialSubscriptionStartDateIOS?: unknown;
  trialSubscriptionStartDateWeb?: unknown;
}

export type PostAuthDestination =
  | { kind: 'onboarding'; pathname: '/onboarding' }
  | { kind: 'internal'; pathname: '/u' | '/u/dashboard' }
  | { kind: 'external'; href: string };

/** Authentication or an OAuth code opens onboarding. A query preview cannot. */
export function onboardingRouteIsOpen(input: {
  authenticated: boolean;
  search: string;
  hash: string;
}): boolean {
  if (input.authenticated) return true;
  return Boolean(new URLSearchParams(String(input.hash || '').replace(/^#/, '')).get('code'));
}

/** Only an explicit backend boolean true marks onboarding as complete. */
export function requiresOnboarding(
  user?: OnboardingRoutingUser | null,
): boolean {
  return Boolean(user) && user?.isOnboardingCompleted !== true;
}

function getMeaningfulSameOriginRedirect(
  uri: string | null | undefined,
  origin: string,
): string | null {
  if (!uri) return null;

  try {
    const parsed = new URL(uri, origin);
    const path = parsed.pathname.replace(/\/+$/, '') || '/';

    if (parsed.origin !== origin || path === '/' || path === '/redirect') {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Resolves the post-authentication destination with incomplete onboarding as
 * the highest-priority rule. Browser preview state is intentionally excluded.
 */
export function resolvePostAuthDestination({
  user,
  redirectUri,
  origin,
}: {
  user?: OnboardingRoutingUser | null;
  redirectUri?: string | null;
  origin: string;
}): PostAuthDestination {
  if (requiresOnboarding(user)) {
    return { kind: 'onboarding', pathname: '/onboarding' };
  }

  const redirect = getMeaningfulSameOriginRedirect(redirectUri, origin);
  if (redirect) {
    return { kind: 'external', href: redirect };
  }

  const hasStartedTrial = Boolean(
    user?.trialSubscriptionStartDateAndroid ||
      user?.trialSubscriptionStartDateIOS ||
      user?.trialSubscriptionStartDateWeb,
  );

  return {
    kind: 'internal',
    pathname: hasStartedTrial ? '/u' : '/u/dashboard',
  };
}
